/**
 * Conversation Service - Servicio de conversaciones y chat
 */

import { ApiServiceBase } from '../core/ApiService';
import type {
  Conversation,
  Message,
  ChatMessageRequest,
  ConversationCreateRequest,
  ConversationUpdateRequest,
} from '../types/conversation';
import { authManager } from '../../../utils/authManager';
import logger from '../../../utils/simpleLogger';

export class ConversationService extends ApiServiceBase {
  /**
   * Obtiene todas las conversaciones del usuario
   */
  async getConversations(): Promise<{ conversations: Conversation[]; count: number }> {
    logger.info('💬 [API] getConversations() called');
    const response = await this.request('/chat/conversations');
    logger.info('💬 [API] getConversations success', {
      count: (response as any).count,
    });
    return response as { conversations: Conversation[]; count: number };
  }

  /**
   * Crea una nueva conversación
   */
  async createConversation(
    conversationData: ConversationCreateRequest
  ): Promise<{ conversation: Conversation; message: string }> {
    logger.info('💬 [API] createConversation() called', { conversationData });
    try {
      const response = await this.request('/chat/conversations', {
        method: 'POST',
        body: JSON.stringify(conversationData),
      });
      logger.info('💬 [API] createConversation success', {
        conversationId: (response as any).conversation?.id,
      });
      return response as { conversation: Conversation; message: string };
    } catch (error) {
      logger.error('❌ [API] createConversation failed', error);
      throw error;
    }
  }

  /**
   * Obtiene una conversación específica con sus mensajes
   */
  async getConversation(
    conversationId: string
  ): Promise<{ conversation: Conversation & { messages: Message[] } }> {
    return this.request(`/chat/conversations/${conversationId}`);
  }

  /**
   * Actualiza una conversación (nombre, pin, archivar)
   */
  async updateConversation(
    conversationId: string,
    data: ConversationUpdateRequest
  ): Promise<{ success: boolean; conversation: Conversation }> {
    return this.request(`/chat/conversations/${conversationId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Elimina una conversación
   */
  async deleteConversation(conversationId: string): Promise<{ message: string }> {
    return this.request(`/chat/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Duplica una conversación con todos sus mensajes
   */
  async duplicateConversation(
    conversationId: string
  ): Promise<{ success: boolean; conversation: Conversation }> {
    return this.request(`/chat/conversations/${conversationId}/duplicate`, {
      method: 'POST',
    });
  }

  /**
   * Busca conversaciones por nombre
   */
  async searchConversations(
    query: string,
    includeArchived?: boolean
  ): Promise<{ conversations: Conversation[]; count: number }> {
    const params = new URLSearchParams({ q: query });
    if (includeArchived) params.set('include_archived', 'true');
    return this.request(`/chat/conversations/search?${params.toString()}`);
  }

  /**
   * Pre-selecciona documentos basado en una query (v2: intent-aware)
   */
  async preSelectDocuments(
    query: string,
    conversationId: string,
    currentDocumentIds?: string[]
  ): Promise<{
    success: boolean;
    document_ids: string[];
    document_names: string[];
    detected_companies: string[];
    count: number;
    confidence?: number;
    strategy?: string;
    reasons?: string[];
    user_prompt_needed?: boolean;
    query_type?: string;
    is_conversational?: boolean;
    action?: 'keep' | 'replace' | 'merge';
  }> {
    logger.info('🎯 [API] preSelectDocuments() called', {
      query,
      conversationId,
      currentDocumentIds: currentDocumentIds?.length,
    });
    return this.request('/chat/pre-select', {
      method: 'POST',
      body: JSON.stringify({
        query,
        conversation_id: conversationId,
        current_document_ids: currentDocumentIds || [],
      }),
    });
  }

  /**
   * Envía un mensaje (sin streaming)
   */
  async sendMessage(messageData: ChatMessageRequest): Promise<{
    user_message: Message;
    assistant_message: Message;
    conversation: Conversation;
    processing_time: number;
    usage_stats?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      word_count: number;
      elapsed_time_seconds: number;
      model_used: string;
      cost_usd: number;
      cost_clp: number;
      cost_breakdown?: {
        input_usd: number;
        output_usd: number;
        input_clp: number;
        output_clp: number;
      };
      timing_breakdown?: Record<string, any>;
    };
    rag_stats?: {
      total_chunks?: number;
      dense_retrieved?: number;
      bm25_retrieved?: number;
      merged_unique?: number;
      after_mmr?: number;
      after_reranking?: number;
      final_with_neighbors?: number;
      page_count?: number;
      auto_scaled_top_k?: number;
    };
    auto_selected_documents?: boolean;
    auto_selected_count?: number;
    auto_selected_document_names?: string[];
    map_results?: Array<{
      batch_number: number;
      response: string;
      citations?: Record<string, any>;
      processing_time?: number;
      num_documents?: number;
    }>;
  }> {
    return this.request('/chat/send', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  /**
   * Envía un mensaje con streaming SSE (Map-Reduce)
   */
  async sendMessageStream(
    messageData: ChatMessageRequest,
    callbacks: {
      onStatus?: (data: { status: string; auto_selected?: boolean; code?: string }) => void;
      onBatchStart?: (data: { batch_number: number; total_batches: number }) => void;
      onBatchToken?: (data: {
        batch_number: number;
        token: string;
        full_response_so_far: string;
      }) => void;
      onToken?: (data: { token: string; full_response_so_far: string }) => void;
      onComplete?: (data: {
        message_id?: string;
        response: string;
        citations: Record<string, any>;
        usage_stats?: Record<string, any>;
        rag_stats?: Record<string, any>;
        user_message_id?: string;
        ai_flow_trace?: Array<{
          stage: string;
          name: string;
          time_ms: number;
          status: string;
          data?: Record<string, any>;
        }>;
      }) => void;
      onBatchComplete?: (data: {
        batch_number: number;
        total_batches?: number;
        message_id?: string;
        response: string;
        citations: Record<string, any>;
        processing_time: number;
        num_documents: number;
        num_chunks?: number;
        finish_reason?: string;
        usage_stats?: {
          prompt_tokens?: number;
          completion_tokens?: number;
          total_tokens?: number;
        };
        rag_stats?: {
          final_chunks?: number;
          num_documents?: number;
          batch_id?: number;
        };
        ai_flow_trace?: Array<{
          stage: string;
          name: string;
          time_ms: number;
          status: string;
          data?: Record<string, any>;
        }>;
      }) => void;
      onBatchError?: (data: { batch_number: number; error: string }) => void;
      onFinalResult?: (data: {
        total_batches: number;
        batches_completed: number;
        message_ids?: string[];
        message_id?: string;
        conversation_id?: string;
        user_message_id?: string;
        response?: string;
        citations?: Record<string, any>;
        total_usage_stats?: {
          prompt_tokens?: number;
          completion_tokens?: number;
          total_tokens?: number;
        };
        total_rag_stats?: {
          total_batches?: number;
          total_messages?: number;
          total_documents?: number;
        };
      }) => void;
      onSuggestion?: (data: {
        message: string;
        suggested_documents: Array<{
          id: string;
          name: string;
          relevance: number;
          preview: string;
        }>;
        reason: string;
      }) => void;
      onError?: (
        error: string | { message: string; retryable?: boolean; category?: string }
      ) => void;
      abortSignal?: AbortSignal;
    }
  ): Promise<void> {
    logger.info('🤖 [API] sendMessageStream() called', {
      conversationId: messageData.conversation_id,
      selectedDocuments: messageData.selected_documents.length,
    });
    const token = authManager.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const url = `${this.baseUrl}/chat/send`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(messageData),
      signal: callbacks.abortSignal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const userMessage =
        errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(userMessage);
    }

    // Check if response is SSE (text/event-stream)
    const contentType = response.headers.get('content-type');
    logger.debug('📡 Response content-type:', contentType);
    if (!contentType || !contentType.includes('text/event-stream')) {
      logger.debug('⚠️ Response is not SSE, parsing as JSON');
      // Not SSE, parse as JSON (fallback to regular response)
      const data = await response.json();
      logger.debug('📦 JSON response received:', data);
      // Handle as single batch complete
      if (callbacks.onBatchComplete && data.assistant_message) {
        callbacks.onBatchComplete({
          batch_number: 1,
          total_batches: 1,
          message_id: data.assistant_message.id,
          response: data.assistant_message.content,
          citations: data.assistant_message.citations || {},
          processing_time: data.processing_time || 0,
          num_documents: data.selected_documents?.length || 0,
          usage_stats: data.usage_stats,
          rag_stats: data.rag_stats,
        });
      }
      if (callbacks.onFinalResult) {
        callbacks.onFinalResult({
          total_batches: 1,
          batches_completed: 1,
          message_ids: data.assistant_message ? [data.assistant_message.id] : [],
          conversation_id: data.conversation_id,
          user_message_id: data.user_message?.id,
          total_usage_stats: data.usage_stats,
          total_rag_stats: data.rag_stats,
        });
      }
      return;
    }

    logger.debug('✅ SSE stream detected, starting to read...');

    // Parse SSE stream
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let buffer = '';
    let currentEvent: string | null = null;

    // Contadores para logs condicionales de tokens (reducir spam en consola)
    const TOKEN_LOG_INTERVAL = 20; // Log cada 20 tokens
    let tokenCount = 0;
    let batchTokenCount = 0;

    // Accumulate tokens for real-time streaming
    let fullResponseSoFar = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim() === '') {
            // Empty line indicates end of event
            if (currentEvent) {
              currentEvent = null;
            }
            continue;
          }

          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
            // Skip noisy token events — throttled logging handles them below
            if (currentEvent !== 'token' && currentEvent !== 'batch_token') {
              logger.debug('🔔 SSE Event:', currentEvent);
            }
          } else if (line.startsWith('data: ')) {
            const dataStr = line.substring(6);
            try {
              const parsed = JSON.parse(dataStr);
              const eventType = parsed.type;
              const eventData = parsed.data;

              // Solo loguear eventos importantes, no todos los eventos (reduce spam)
              if (eventType !== 'token' && eventType !== 'batch_token') {
                logger.debug('📦 SSE Data received:', { eventType, eventData });
              }

              // Route to appropriate callback
              switch (eventType) {
                case 'processing_start':
                  logger.debug('✅ Processing started on backend');
                  break;
                case 'analyzing':
                  logger.debug('✅ Pipeline analyzing documents');
                  break;
                case 'batch_start':
                  logger.debug('✅ Calling onBatchStart with:', eventData);
                  callbacks.onBatchStart?.(eventData);
                  break;
                case 'batch_token':
                  batchTokenCount++;
                  if (batchTokenCount % TOKEN_LOG_INTERVAL === 0 || batchTokenCount === 1) {
                    logger.debug(
                      `✅ Batch token ${batchTokenCount} (log cada ${TOKEN_LOG_INTERVAL})`,
                      {
                        batchNumber: eventData?.batch_number,
                        tokenLength: eventData?.token?.length || 0,
                      }
                    );
                  }
                  callbacks.onBatchToken?.(eventData);
                  break;
                case 'token':
                  tokenCount++;
                  // Accumulate token into full response
                  fullResponseSoFar += eventData?.token || '';

                  if (tokenCount % TOKEN_LOG_INTERVAL === 0 || tokenCount === 1) {
                    logger.debug(`✅ Token ${tokenCount} (log cada ${TOKEN_LOG_INTERVAL})`, {
                      tokenLength: eventData?.token?.length || 0,
                      accumulatedLength: fullResponseSoFar.length,
                    });
                  }
                  // Pass accumulated response to callback
                  callbacks.onToken?.({
                    token: eventData?.token || '',
                    full_response_so_far: fullResponseSoFar,
                  });
                  break;
                case 'complete':
                  logger.info('✅ [SSE] complete event received:', {
                    eventDataKeys: Object.keys(eventData || {}),
                    messageId: eventData?.message_id,
                    conversationId: eventData?.conversation_id,
                    hasResponse: !!eventData?.response,
                    responseLength: eventData?.response?.length || 0,
                  });
                  if (tokenCount > 0) {
                    logger.info(
                      `📊 Streaming completado: ${tokenCount} tokens procesados (logs cada ${TOKEN_LOG_INTERVAL})`
                    );
                  }
                  callbacks.onComplete?.(eventData);
                  break;
                case 'batch_complete':
                  logger.debug('✅ Calling onBatchComplete with:', eventData);
                  callbacks.onBatchComplete?.(eventData);
                  break;
                case 'batch_error':
                  logger.debug('❌ Calling onBatchError with:', eventData);
                  callbacks.onBatchError?.(eventData);
                  break;
                case 'final_result':
                  logger.debug('✅ Calling onFinalResult with:', eventData);
                  if (batchTokenCount > 0) {
                    logger.info(
                      `📊 Map-Reduce completado: ${batchTokenCount} tokens procesados en total (logs cada ${TOKEN_LOG_INTERVAL})`
                    );
                  }
                  callbacks.onFinalResult?.(eventData);
                  break;
                case 'error':
                  logger.debug('❌ Calling onError with:', eventData);
                  // Pass structured error data if available (retryable, category)
                  if (eventData.category || eventData.retryable !== undefined) {
                    callbacks.onError?.({
                      message: eventData.error || 'Unknown error',
                      retryable: eventData.retryable,
                      category: eventData.category,
                    });
                  } else {
                    callbacks.onError?.(eventData.error || 'Unknown error');
                  }
                  break;
                case 'status':
                  // Status updates from backend (e.g., "Clasificando consulta...", "Buscando documentos...")
                  logger.debug('📊 Status update:', eventData?.status || eventData);
                  callbacks.onStatus?.(eventData);
                  break;
                case 'suggestion':
                  // Fallback document suggestions when confidence is low
                  logger.info('💡 [SSE] Document suggestion received:', {
                    message: eventData?.message,
                    documentCount: eventData?.suggested_documents?.length || 0,
                    reason: eventData?.reason,
                  });
                  callbacks.onSuggestion?.(eventData);
                  break;
                default:
                  logger.warn('⚠️ Unknown event type received via SSE', { eventType, eventData });
              }
            } catch (e) {
              logger.error('❌ [API] Error parsing SSE data', { error: e, dataStr });
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      callbacks.onError?.(error instanceof Error ? error.message : 'Stream error');
      throw error;
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Reader may already be released
      }
    }
  }

  /**
   * Califica un mensaje
   */
  async rateMessage(
    messageId: string,
    rating: number,
    feedback?: string
  ): Promise<{ message: string }> {
    return this.request(`/chat/messages/${messageId}/rating`, {
      method: 'POST',
      body: JSON.stringify({ rating, feedback }),
    });
  }

  /**
   * Limpia el contexto de una conversación
   */
  async clearConversationContext(conversationId: string): Promise<{
    success: boolean;
    message: string;
    excluded_count: number;
  }> {
    return this.request(`/chat/conversations/${conversationId}/clear-context`, {
      method: 'POST',
    });
  }

  /**
   * Elimina todos los mensajes de una conversación
   */
  async deleteConversationMessages(conversationId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/chat/conversations/${conversationId}/messages`, {
      method: 'DELETE',
    });
  }

  /**
   * Elimina un mensaje
   */
  async deleteMessage(messageId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/chat/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Alterna el contexto de un mensaje (incluir/excluir)
   */
  async toggleMessageContext(messageId: string): Promise<{
    success: boolean;
    excluded_from_context: boolean;
    message: string;
  }> {
    return this.request(`/chat/messages/${messageId}/toggle-context`, {
      method: 'POST',
    });
  }

  /**
   * Crea un mensaje manualmente
   */
  async createMessage(messageData: {
    conversation_id: string;
    content: string;
    role: 'user' | 'assistant';
    citations?: Record<string, any>;
    usage_stats?: Record<string, any>;
    rag_stats?: Record<string, any>;
    selected_documents?: string[];
  }): Promise<{
    success: boolean;
    message_id: string;
  }> {
    logger.info('📤 [API] createMessage called:', {
      conversation_id: messageData.conversation_id,
      role: messageData.role,
      contentLength: messageData.content?.length || 0,
      hasUsageStats: !!messageData.usage_stats,
      hasRagStats: !!messageData.rag_stats,
    });

    const startTime = Date.now();

    try {
      const result = await this.request<{
        success: boolean;
        message_id: string;
      }>('/chat/messages', {
        method: 'POST',
        body: JSON.stringify(messageData),
      });

      const elapsedTime = Date.now() - startTime;
      logger.info('✅ [API] createMessage success:', {
        message_id: result.message_id,
        success: result.success,
        elapsedTime: `${elapsedTime}ms`,
      });

      return result;
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      logger.error('❌ [API] createMessage failed:', {
        error: error instanceof Error ? error.message : String(error),
        conversation_id: messageData.conversation_id,
        role: messageData.role,
        elapsedTime: `${elapsedTime}ms`,
      });
      throw error;
    }
  }

  /**
   * Guarda un mensaje usando el endpoint correcto /saved-messages
   */
  async saveMessage(
    messageId: string,
    category?: string
  ): Promise<{
    success: boolean;
    message: string;
    saved_count: number;
  }> {
    const result = await this.request<{ success: boolean; saved_message: any }>('/saved-messages', {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        tags: category ? [category] : undefined,
      }),
    });
    return { success: result.success, message: 'Message saved', saved_count: 1 };
  }

  /**
   * Des-guarda un mensaje buscando el registro en /saved-messages por message_id
   */
  async unsaveMessage(messageId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const savedData = await this.request<{
      messages: Array<{ id: string; message_id: string }>;
    }>('/saved-messages');
    const match = (savedData.messages || []).find((s) => s.message_id === messageId);
    if (match) {
      await this.request(`/saved-messages/${match.id}`, { method: 'DELETE' });
    }
    return { success: true, message: 'Message unsaved' };
  }

  /**
   * Edita un mensaje
   */
  async editMessage(messageId: string): Promise<{
    success: boolean;
    message: string;
    original_content: string;
    deleted_messages: number;
  }> {
    return this.request(`/chat/messages/${messageId}/edit`, {
      method: 'PUT',
    });
  }

  /**
   * Obtiene información de configuración para la burbuja de configuración
   * Si message_id está presente, calcula configuración histórica
   */
  async getConfigInfo(data: {
    document_ids: string[];
    conversation_id?: string;
    message_id?: string;
  }): Promise<{
    success: boolean;
    config_info?: {
      model: string;
      model_display_name: string;
      max_context_tokens: number;
      temperature: number;
      token_breakdown: {
        documents_tokens: number;
        conversation_tokens: number;
        system_tokens: number;
        available_tokens: number;
        total_used: number;
      };
      selected_documents: Array<{
        id: string;
        name: string;
        company_name?: string;
        document_type?: string;
        document_period?: string;
        document_code?: string;
        page_count?: number;
        estimated_tokens: number;
      }>;
      available_models: Array<{
        id: string;
        name: string;
        max_tokens: number;
        context: string;
      }>;
    };
    error?: string;
  }> {
    return this.request('/chat/config-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Actualiza configuración rápidamente desde la burbuja
   */
  async updateConfigQuick(data: {
    model?: string;
    temperature?: number;
    document_ids?: string[];
  }): Promise<{
    success: boolean;
    message?: string;
    updated_config?: any;
    error?: string;
  }> {
    return this.request('/chat/config-update', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}
